/**
 * localStorage Cache Utilities
 *
 * Utilities for quick-load pattern with localStorage caching.
 * See QUICK_LOAD_PATTERN.md for usage guide.
 *
 * Why localStorage (not sessionStorage)?
 * - Persists across tab close/reload - no blank screens
 * - Better UX - instant load even after browser restart
 * - Matches SchoolContext pattern - consistent architecture
 * - Real-time listeners keep fresh - no stale data concerns
 */

import { isDevToolsGateOpen } from './devToolsGate';

/**
 * Quick-load cache utilities for localStorage operations
 */
export const quickLoadCache = {
  /**
   * Save data to localStorage with metadata
   * @param {string|null} key - Cache key (skips save if key is null)
   * @param {*} data - Data to cache
   * @param {Object} metadata - Optional metadata (e.g., { limit: 30, stats: {...} })
   */
  save: (key, data, metadata = {}) => {
    // Guard: skip save if key is missing (e.g., when schoolId not available yet)
    if (!key) return;

    try {
      const cacheEntry = {
        data,
        metadata,
        timestamp: Date.now()
      };
      localStorage.setItem(key, JSON.stringify(cacheEntry));

      const dataInfo = Array.isArray(data)
        ? `${data.length} items`
        : typeof data === 'object'
        ? `${Object.keys(data).length} keys`
        : 'scalar';

      console.log(`[Cache] 💾 Saved ${key}:`, {
        data: dataInfo,
        ...metadata
      });
    } catch (error) {
      if (error.name === 'QuotaExceededError') {
        console.warn(`[Cache] ⚠️ Quota exceeded for ${key} - cache not saved`);
      } else {
        console.error(`[Cache] ❌ Error saving ${key}:`, error);
      }
    }
  },

  /**
   * Load data from localStorage
   * @param {string|null} key - Cache key (returns null if key is null)
   * @returns {Object|null} - { data, metadata, timestamp } or null if not found
   */
  load: (key) => {
    // Guard: return null if key is missing (e.g., when schoolId not available yet)
    if (!key) return null;

    try {
      const cached = localStorage.getItem(key);
      if (!cached) {
        console.log(`[Cache] ⚠️ MISS ${key} - no cached data`);
        return null;
      }

      const cacheEntry = JSON.parse(cached);
      const cacheAge = (Date.now() - cacheEntry.timestamp) / 1000;

      const dataInfo = Array.isArray(cacheEntry.data)
        ? `${cacheEntry.data.length} items`
        : typeof cacheEntry.data === 'object'
        ? `${Object.keys(cacheEntry.data).length} keys`
        : 'scalar';

      console.log(`[Cache] 📦 HIT ${key}:`, {
        cacheAgeSec: cacheAge.toFixed(1),
        data: dataInfo
      });

      return cacheEntry;
    } catch (error) {
      console.error(`[Cache] ❌ Error loading ${key}:`, error);
      // Clear corrupted cache
      try {
        localStorage.removeItem(key);
        console.log(`[Cache] 🗑️ Cleared corrupted cache for ${key}`);
      } catch (e) {
        // Ignore cleanup errors
      }
      return null;
    }
  },

  /**
   * Clear specific cache key
   * @param {string|null} key - Cache key to clear (skips if key is null)
   */
  clear: (key) => {
    // Guard: skip clear if key is missing
    if (!key) return;

    try {
      localStorage.removeItem(key);
      console.log(`[Cache] 🗑️ Cleared ${key}`);
    } catch (error) {
      console.error(`[Cache] ❌ Error clearing ${key}:`, error);
    }
  },

  /**
   * Clear all caches matching a pattern
   * @param {string} pattern - Pattern to match (e.g., 'swan_' clears all swan caches)
   */
  clearPattern: (pattern) => {
    try {
      const keys = Object.keys(localStorage);
      const matchingKeys = keys.filter(key => key.includes(pattern));

      matchingKeys.forEach(key => {
        localStorage.removeItem(key);
      });

      console.log(`[Cache] 🗑️ Cleared ${matchingKeys.length} caches matching "${pattern}"`);
    } catch (error) {
      console.error(`[Cache] ❌ Error clearing pattern ${pattern}:`, error);
    }
  },

  /**
   * Get cache statistics
   * @returns {Object} - { totalKeys, totalSize, keys: [...] }
   */
  getStats: () => {
    try {
      const keys = Object.keys(localStorage);
      const swanKeys = keys.filter(key => key.startsWith('swan_'));

      let totalSize = 0;
      const keyInfo = swanKeys.map(key => {
        const value = localStorage.getItem(key);
        const size = new Blob([value]).size;
        totalSize += size;

        return {
          key,
          size: `${(size / 1024).toFixed(2)} KB`,
          age: (() => {
            try {
              const entry = JSON.parse(value);
              const age = (Date.now() - entry.timestamp) / 1000;
              return `${age.toFixed(1)}s`;
            } catch {
              return 'unknown';
            }
          })()
        };
      });

      return {
        totalKeys: swanKeys.length,
        totalSize: `${(totalSize / 1024).toFixed(2)} KB`,
        keys: keyInfo
      };
    } catch (error) {
      console.error('[Cache] ❌ Error getting stats:', error);
      return { totalKeys: 0, totalSize: '0 KB', keys: [] };
    }
  }
};

/**
 * Generate a cache key for a collection
 * @param {string} collectionName - Firestore collection name
 * @param {string} schoolId - School ID for scoping (returns null if missing)
 * @param {string} [suffix] - Optional suffix for variants (e.g., 'pending', 'completed')
 * @returns {string|null} - Cache key or null if schoolId is missing
 */
export const getCacheKey = (collectionName, schoolId, suffix = '') => {
  // Guard: return null if schoolId is missing (matches old inline pattern)
  if (!schoolId) return null;

  const parts = ['swan', collectionName, schoolId];
  if (suffix) parts.push(suffix);
  return parts.join('_');
};

/**
 * Make cache utilities available in browser console for debugging
 */
if (isDevToolsGateOpen()) {
  window.quickLoadCache = quickLoadCache;
  console.log('📦 Quick-load cache utilities available: window.quickLoadCache');
}
